import bbox from '@turf/bbox';
import debug from 'debug';
import { index } from 'fp-ts/lib/Array';
import { Option, none, some } from 'fp-ts/lib/Option';
import { Extent } from 'ol/extent';
import { __forceRefreshState } from '../app';
import { DirectGeometryObject, GeometryType, Position } from '../source';

const logger = debug('sdi:map/mini');

type BBox2d = [number, number, number, number] | Extent;
type Size = [number, number];

export type MiniStep =
    | { step: 'loading' }
    | { step: 'failed' }
    | { step: 'loaded'; data: string };

export const miniMapLoading = (): MiniStep => ({ step: 'loading' });
export const miniMapFailed = (): MiniStep => ({ step: 'failed' });
export const miniMapLoaded = (data: string): MiniStep => ({
    step: 'loaded',
    data,
});

// tslint:disable-next-line:variable-name
const WMSBaseRequest = {
    SERVICE: 'WMS',
    VERSION: '1.1.1',
    REQUEST: 'GetMap',
    FORMAT: 'image/png',
    TRANSPARENT: 'true',
    LAYERS: 'UrbISFrenchLabeledGray',
    WIDTH: 256,
    HEIGHT: 256,
    SRS: 'EPSG:31370',
    STYLES: '',
    BBOX: '',
};

export type WMSRequest = typeof WMSBaseRequest;
export type WMSRequestKey = keyof WMSRequest;

const encodeRequest = (r: WMSRequest) =>
    Object.keys(r)
        .map(
            (k: WMSRequestKey) => `${k}=${encodeURIComponent(r[k].toString())}`,
        )
        .join('&');

export const mapCoordinates = <T>(
    geom: DirectGeometryObject,
    f: (p: Position) => T,
) => {
    switch (geom.type) {
        case 'Point':
            return f(geom.coordinates);
        case 'MultiPoint':
            return geom.coordinates.map(f);
        case 'LineString':
            return geom.coordinates.map(f);
        case 'MultiLineString':
            return geom.coordinates.map(l => l.map(f));
        case 'Polygon':
            return geom.coordinates.map(l => l.map(f));
        case 'MultiPolygon':
            return geom.coordinates.map(p => p.map(l => l.map(f)));
    }
};

const _180_DEGREES = 180.0;
const _100_METERS = 100;
const _0_1_DEGREE = 0.1;
const MINIMAP_PADDING = 1.3;

const buffer = (width: number, geom: DirectGeometryObject) => {
    switch (geom.type) {
        case 'Point':
            // > 180.0 — distinguishes the coordinate system.
            // Longitude in WGS84 (EPSG:4326) is in [-180, 180], so > 180 means the
            // coordinates are in a projected CRS like EPSG:31370 (Belgian Lambert),
            // where x values are in the hundreds of thousands of meters.
            return geom.coordinates[0] > _180_DEGREES
                ? _100_METERS
                : _0_1_DEGREE;
        case 'MultiPoint':
            return geom.coordinates[0][0] > _180_DEGREES
                ? Math.max(width * MINIMAP_PADDING, _100_METERS)
                : Math.max(width * MINIMAP_PADDING, _0_1_DEGREE);
        case 'LineString':
        case 'MultiLineString':
        case 'Polygon':
        case 'MultiPolygon':
            return width * MINIMAP_PADDING;
    }
};

const circle = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
) => ctx.arc(x, y, radius, 0, 2 * Math.PI);

const drawPoint = (ctx: CanvasRenderingContext2D) => (p: Position) => {
    const [x, y] = p;
    logger(`drawPoint (${x}, ${y});`);
    circle(ctx, x, y, 10);
};

const drawMultiPoint = (ctx: CanvasRenderingContext2D) => (p: Position) => {
    const [x, y] = p;
    const half = 5;
    logger(`drawMultiPoint (${x}, ${y});`);
    ctx.rect(x - half, y - half, half * 2, half * 2);
};

const drawLine = (ctx: CanvasRenderingContext2D) => (p: Position[]) => {
    index(0, p).map(([x, y]) => ctx.moveTo(x, y));
    p.slice(1).map(([x, y]) => ctx.lineTo(x, y));
    ctx.stroke();
};

const drawPolygon = (ctx: CanvasRenderingContext2D) => (p: Position[][]) => {
    p.map(inner => {
        index(0, inner).map(([x, y]) => ctx.moveTo(x, y));
        inner.slice(1).map(([x, y]) => ctx.lineTo(x, y));
        ctx.stroke();
    });
};

const defaultBeforePath = (
    _ctx: CanvasRenderingContext2D,
    _gt: GeometryType,
) => {};

const defaultAfterPath = (ctx: CanvasRenderingContext2D, gt: GeometryType) => {
    switch (gt) {
        case 'Point':
        case 'MultiPoint':
            ctx.fill();
            break;

        case 'LineString':
        case 'MultiLineString':
            ctx.stroke();
            break;

        case 'Polygon':
        case 'MultiPolygon':
            ctx.stroke();
            break;
    }
};

const draw = (
    ctx: CanvasRenderingContext2D,
    geom: DirectGeometryObject,
    beforePath: (ctx: CanvasRenderingContext2D, gt: GeometryType) => void,
    afterPath: (ctx: CanvasRenderingContext2D, gt: GeometryType) => void,
) => {
    beforePath(ctx, geom.type);
    switch (geom.type) {
        case 'Point':
            drawPoint(ctx)(geom.coordinates);
            break;
        case 'MultiPoint':
            geom.coordinates.map(drawMultiPoint(ctx));
            break;
        case 'LineString':
            drawLine(ctx)(geom.coordinates);
            break;
        case 'MultiLineString':
            geom.coordinates.map(drawLine(ctx));
            break;
        case 'Polygon':
            drawPolygon(ctx)(geom.coordinates);
            break;
        case 'MultiPolygon':
            geom.coordinates.map(drawPolygon(ctx));
            break;
    }
    afterPath(ctx, geom.type);
};

const drawGeomOnImage = (
    image: CanvasImageSource,
    geom: Readonly<DirectGeometryObject>,
    box: BBox2d,
    sz: Size,
    beforePath: (ctx: CanvasRenderingContext2D, gt: GeometryType) => void,
    afterPath: (ctx: CanvasRenderingContext2D, gt: GeometryType) => void,
) => {
    const [minx, miny, maxx, maxy] = box;
    logger(`drawGeomOnImage (${minx}. ${miny})`);
    const canvas = document.createElement('canvas');
    const hscale = sz[0] / (maxx - minx);
    const vscale = sz[1] / (maxy - miny);
    canvas.width = sz[0];
    canvas.height = sz[1];
    logger(`drawGeomOnImage (${hscale}. ${vscale})`);
    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.drawImage(image, 0, 0);
        const coordinates = mapCoordinates(geom, p => [
            (p[0] - minx) * hscale,
            sz[1] - (p[1] - miny) * vscale,
        ]);
        const transformed = { ...geom, coordinates } as DirectGeometryObject;

        ctx.beginPath();
        draw(ctx, transformed, beforePath, afterPath);
        return some(canvas.toDataURL('image/png'));
    }
    return none;
};

const hashCoordinate = (p: Position) => `${p[0]},${p[1]}`;

const hashGeom = (geom: Readonly<DirectGeometryObject>) => {
    switch (geom.type) {
        case 'Point':
            return hashCoordinate(geom.coordinates);
        case 'LineString':
            return geom.coordinates.map(hashCoordinate).join('~');
        case 'Polygon':
            return geom.coordinates
                .map(multi => multi.map(hashCoordinate).join('+'))
                .join('~');
        case 'MultiPoint':
            return geom.coordinates.map(hashCoordinate).join('~');
        case 'MultiLineString':
            return geom.coordinates
                .map(multi => multi.map(hashCoordinate).join('+'))
                .join('~');
        case 'MultiPolygon':
            return geom.coordinates
                .map(multi =>
                    multi
                        .map(inner => inner.map(hashCoordinate).join('+'))
                        .join('~'),
                )
                .join('/');
    }
};

/**
 * Build a static image based on a
 * geometry and a  WMS URL
 */
export const miniMap =
    (url: string, options: Partial<WMSRequest>) =>
    (geometry: Readonly<DirectGeometryObject>): Promise<Option<string>> => {
        const [minxOrig, minyOrig, maxx, maxy] = bbox(geometry);
        const widthOrig = maxx - minxOrig;
        const heightOrig = maxy - minyOrig;
        const width = buffer(widthOrig, geometry);
        const height = buffer(heightOrig, geometry);
        const sideMax = Math.max(width, height);
        const sideMin = Math.min(width, height);
        const minx = minxOrig - (width - widthOrig) / 2;
        const miny = minyOrig - (height - heightOrig) / 2;
        const abbox: BBox2d = [
            minx - (sideMax - sideMin) / 2,
            miny - (sideMax - sideMin) / 2,
            minx + sideMax,
            miny + sideMax,
        ];
        const bboxString = abbox.map(c => c.toFixed(2)).join(',');
        const parameters = { ...WMSBaseRequest, ...options, BBOX: bboxString };
        const querystring = encodeRequest(parameters);
        const requestUrl = `${url}?${querystring}`;
        logger(`URL: ${requestUrl}`);
        return fetch(requestUrl)
            .then(response => {
                if (response.ok) {
                    return response.blob();
                }
                throw new Error(response.statusText);
            })
            .then(blob => createImageBitmap(blob))
            .then(image =>
                drawGeomOnImage(
                    image,
                    geometry,
                    abbox,
                    [parameters.WIDTH, parameters.HEIGHT],
                    defaultBeforePath,
                    defaultAfterPath,
                ),
            )
            .catch(err => {
                logger(`fetch(${requestUrl}) error ${err}`);
                return none;
            });
    };

export default miniMap;

export const miniMap2 = (url: string, options: Partial<WMSRequest>) => {
    const cache: Record<string, MiniStep> = {};

    return (geometry: Readonly<DirectGeometryObject>): MiniStep => {
        const hash = hashGeom(geometry);
        if (!(hash in cache)) {
            cache[hash] = miniMapLoading();
            const [minxOrig, minyOrig, maxx, maxy] = bbox(geometry);
            const widthOrig = maxx - minxOrig;
            const heightOrig = maxy - minyOrig;
            const width = buffer(widthOrig, geometry);
            const height = buffer(heightOrig, geometry);
            const sideMax = Math.max(width, height);
            const sideMin = Math.min(width, height);
            const minx = minxOrig - (width - widthOrig) / 2;
            const miny = minyOrig - (height - heightOrig) / 2;
            const abbox: BBox2d = [
                minx - (sideMax - sideMin) / 2,
                miny - (sideMax - sideMin) / 2,
                minx + sideMax,
                miny + sideMax,
            ];
            const bboxString = abbox.map(c => c.toFixed(2)).join(',');
            const parameters = {
                ...WMSBaseRequest,
                ...options,
                BBOX: bboxString,
            };
            const querystring = encodeRequest(parameters);
            const requestUrl = `${url}?${querystring}`;

            fetch(requestUrl)
                .then(response => {
                    if (response.ok) {
                        return response.blob();
                    }
                    throw new Error(response.statusText);
                })
                .then(blob => createImageBitmap(blob))
                .then(image => {
                    drawGeomOnImage(
                        image,
                        geometry,
                        abbox,
                        [parameters.WIDTH, parameters.HEIGHT],
                        defaultBeforePath,
                        defaultAfterPath,
                    ).foldL(
                        () => (cache[hash] = miniMapFailed()),
                        data => (cache[hash] = miniMapLoaded(data)),
                    );
                    __forceRefreshState();
                })
                .catch(err => {
                    logger(`fetch(${requestUrl}) error ${err}`);
                    cache[hash] = miniMapFailed();
                    __forceRefreshState();
                });
        }
        return cache[hash];
    };
};

export const miniMap3 = (
    url: string,
    options: Partial<WMSRequest>,
    beforePath: (ctx: CanvasRenderingContext2D, gt: GeometryType) => void,
    afterPath: (ctx: CanvasRenderingContext2D, gt: GeometryType) => void,
) => {
    const cache: Record<string, MiniStep> = {};

    return (
        geometry: Readonly<DirectGeometryObject>,
        bbox: BBox2d,
        [width, height]: [number, number],
    ): MiniStep => {
        const hash = hashGeom(geometry);
        if (!(hash in cache)) {
            cache[hash] = miniMapLoading();

            const bboxString = bbox.map(c => c.toFixed(2)).join(',');
            const parameters = {
                ...WMSBaseRequest,
                ...options,
                BBOX: bboxString,
                WIDTH: width,
                HEIGHT: height,
            };
            const querystring = encodeRequest(parameters);
            const requestUrl = `${url}?${querystring}`;

            fetch(requestUrl)
                .then(response => {
                    if (response.ok) {
                        return response.blob();
                    }
                    throw new Error(response.statusText);
                })
                .then(blob => createImageBitmap(blob))
                .then(image => {
                    drawGeomOnImage(
                        image,
                        geometry,
                        bbox,
                        [parameters.WIDTH, parameters.HEIGHT],
                        beforePath,
                        afterPath,
                    ).foldL(
                        () => (cache[hash] = miniMapFailed()),
                        data => (cache[hash] = miniMapLoaded(data)),
                    );
                    __forceRefreshState();
                })
                .catch(err => {
                    logger(`fetch(${requestUrl}) error ${err}`);
                    cache[hash] = miniMapFailed();
                    __forceRefreshState();
                });
        }
        return cache[hash];
    };
};

logger('loaded');
